import React, { useState, useRef, useEffect, useMemo } from 'react';
import Header from '../../components/common/header/Header';
import './Dashboard.scss'; // Import global SCSS
import vehicleListDataRaw from '../../data/dashboard.json';
import VehicleMap from '../../components/Map/vehicleMap/VehicleMap';
import StatusBar from '../../components/Map/statusBar/StatusBar';
import VehicleDetailedPopup from '../../components/Map/vehicleDetailedPopup/VehicleDetailedPopup';
import { VehicleInfo } from '../../types/vehicle.type';
import VehicleCard from '../../components/vehicleCard/VehicleCard';
import SearchBar from '../../components/common/searchBar/SearchBar';

import { getRandomDate, getRelativeDate } from '../../utils/date.utils';

const Dashboard: React.FC = () => {
    const [showVehicleDetailPopup, setShowVehicleDetailPopup] = useState<boolean>(false);
    const [selectedVehicle, setSelectedVehicle] = useState<VehicleInfo | null>(null);
    const [activeMarkerVehicleOnMap, setActiveMarkerVehicleOnMap] = React.useState<VehicleInfo | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    // Add refs for sidebar and cards
    const sidebarRef = useRef<HTMLDivElement>(null);
    const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

    const statusColors: Record<string, string> = {
        Moving: '#5DF46C',
        Idle: '#E8F45D',
        Stopped: '#D50000',
        Alerts: '#FF3030',
        Maintenance: '#FF6B00',
        Offline: '#9A9A9A',
        Parked: '#00B2FF',
    };

    // Generate dynamic timestamps and process vehicle data
    const processedVehicleData = useMemo(() =>
        vehicleListDataRaw.map((v: any) => ({
            ...v,
            vehicleBasicInfo: {
                ...v.vehicleBasicInfo,
                timestamp: getRandomDate()
            },
            vehicleDetailInfo: {
                ...v.vehicleDetailInfo,
                vehicleSpecs: {
                    ...v.vehicleDetailInfo.vehicleSpecs,
                    nextMaintenance: getRelativeDate(30).split('T')[0]
                },
                alerts: (v.vehicleDetailInfo.alerts || []).map((alert: any) => ({
                    ...alert,
                    timestamp: getRandomDate(5) // Alerts within last 5 days
                })),
                maintenanceHistory: (v.vehicleDetailInfo.maintenanceHistory || []).map((m: any) => ({
                    ...m,
                    date: getRandomDate(15) // Maintenance within last 15 days
                })),
                reportData: (v.vehicleDetailInfo.reportData || []).map((r: any, idx: number) => ({
                    ...r,
                    predicted_date: getRelativeDate(idx + 1), // Future dates
                    reported_date: getRandomDate(5), // Recent dates
                    resolved_on: r.status === 'RESOLVED' ? getRandomDate(1) : ''
                }))
            }
        })), []
    );

    // Compute status counts from vehicleListData
    const statusCounts = processedVehicleData.reduce((acc, { vehicleBasicInfo, vehicleDetailInfo }) => {
        const status = vehicleBasicInfo.status;
        if (!acc[status]) {
            acc[status] = { count: 0, color: statusColors[status] || '#999' };
        }
        acc[status].count += 1;
        // Alerts count (special logic)
        if (vehicleDetailInfo && Array.isArray(vehicleDetailInfo.alerts) && vehicleDetailInfo.alerts.length > 0) {
            if (!acc['Alerts']) {
                acc['Alerts'] = { count: 0, color: statusColors['Alerts'] };
            }
            acc['Alerts'].count += 1;
        }
        return acc;
    }, {} as { [key: string]: { count: number; color: string } });

    // Normalize vehicleListData to ensure charts properties are always arrays
    const vehicleListData: VehicleInfo[] = processedVehicleData.map((v: any) => {
        const charts = v.vehicleDetailInfo.charts;
        // Find the most recent alert for this vehicle
        let recentAlert = null;
        if (Array.isArray(v.vehicleDetailInfo.alerts) && v.vehicleDetailInfo.alerts.length > 0) {
            recentAlert = v.vehicleDetailInfo.alerts.reduce((latest: import('../../types/vehicle.type').VehicleAlert, alert: import('../../types/vehicle.type').VehicleAlert) => {
                return new Date(alert.timestamp) > new Date(latest.timestamp) ? alert : latest;
            }, v.vehicleDetailInfo.alerts[0]);
        }
        return {
            ...v,
            vehicleDetailInfo: {
                ...v.vehicleDetailInfo,
                charts: {
                    ...charts,
                    engineTemperature: Array.isArray(charts.engineTemperature) ? charts.engineTemperature : [charts.engineTemperature],
                    engineHours: Array.isArray(charts.engineHours) ? charts.engineHours : [charts.engineHours],
                    engineLoad: Array.isArray(charts.engineLoad) ? charts.engineLoad : [charts.engineLoad],
                }
            },
            recentAlert
        };
    });

    // Filter vehicles based on search query
    const filteredVehicles = vehicleListData.filter(v =>
        v.vehicleBasicInfo.vehicle.toLowerCase().includes(searchQuery.toLowerCase()) ||
        v.vehicleBasicInfo.deviceId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        v.vehicleBasicInfo.licensePlate.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Auto-scroll to selected card
    useEffect(() => {
        if (selectedVehicle) {
            const index = filteredVehicles.findIndex(v => v.vehicleId === selectedVehicle.vehicleId);
            const card = cardRefs.current[index];
            if (card && sidebarRef.current) {
                card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    }, [selectedVehicle, filteredVehicles]);

    return (
        <>
            <main className="dashboard">
                <Header />
                <section className="content">
                    <article className="vehicle-map-container" style={{ height: 'calc(100vh - 86px)', position: 'relative' }}>
                        <StatusBar statusCounts={statusCounts} />
                        <VehicleMap
                            vehicleList={vehicleListData}
                            // eslint-disable-next-line no-sequences
                            onClickMarker={(v) => (setActiveMarkerVehicleOnMap(v), setSelectedVehicle(v))}
                            activeMarkerVehicleOnMapProp={activeMarkerVehicleOnMap}
                            // eslint-disable-next-line no-sequences
                            onVehicleCardMoreInfoClick={(v) => (setSelectedVehicle(v), setShowVehicleDetailPopup(true))}
                        />
                    </article>
                    <aside className="sidebar-container" ref={sidebarRef}>
                        <header className="input-group mb-3">
                            <SearchBar value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                        </header>
                        <footer className="sidebar-list" style={{ maxHeight: 'calc(100vh - 165px)' }}>
                            {filteredVehicles.map((v, index) => (
                                <div
                                    key={index}
                                    ref={el => { cardRefs.current[index] = el; }}
                                >
                                    <VehicleCard
                                        vehicleId={v.vehicleId}
                                        vehicleBasicInfo={v.vehicleBasicInfo}
                                        vehicleDetailInfo={v.vehicleDetailInfo}
                                        expandMetrics={selectedVehicle?.vehicleId === v?.vehicleId}
                                        onMoreInfoClick={() => setShowVehicleDetailPopup(true)}
                                        onClickVehicleCard={() => {
                                            setSelectedVehicle(v);
                                            setActiveMarkerVehicleOnMap(v);
                                        }}
                                    />
                                </div>
                            ))}
                        </footer>
                    </aside>
                </section>
            </main>
            {/* Popup rendered here in Dashboard */}
            {(showVehicleDetailPopup && selectedVehicle) && (
                <VehicleDetailedPopup
                    detailInfo={selectedVehicle.vehicleDetailInfo}
                    basicInfo={selectedVehicle.vehicleBasicInfo}
                    onClose={() => setShowVehicleDetailPopup(false)}
                />
            )}
        </>
    );
};

export default Dashboard;
